<template>
  <div class="relative flex flex-col h-screen">
    <img :src="getImage('mascot')" class="fixed papsi-mascot" />

    <div class="flex flex-1 lg:flex-row flex-col">
      <SidebarSection>
        <LabelControl type="message" :content="$t('pma001')" />
      </SidebarSection>

      <ContentSection :show_logout="false">
        <div>
          <LabelControl class="mb-10" type="heading" :content="$t('pma002')" />
          <div
            class="bg-grey-lighter flex flex-col justify-center p-10 rounded-2xl"
          >
            <!-- Username Input -->
            <div
              class="bg-white rounded-xl px-8 py-6 mb-10 text-4xl"
              @click="openKeyboard('username')"
            >
              <span :class="form.username ? 'text-black' : 'text-grey-dark'">
                {{ form.username || $t("pma003") }}
              </span>
            </div>

            <!-- Password Input -->
            <div
              @click="openKeyboard('password')"
              class="bg-white rounded-xl px-8 py-6 text-4xl text-grey-dark relative"
            >
              <span
                v-if="!show_password"
                :class="form.password ? 'text-black' : 'text-grey-dark'"
              >
                {{
                  form.password
                    ? "•".repeat(form.password.length)
                    : $t("pma004")
                }}
              </span>

              <span
                v-else
                :class="form.password ? 'text-black' : 'text-grey-dark'"
              >
                {{ form.password || $t("pma004") }}
              </span>

              <IconControl
                @click.stop="togglePasswordVisibility"
                :id="show_password ? 'password_visible' : 'password_invisible'"
                :width="40"
                :height="40"
                class="absolute papsi-eye mt-2"
              />
            </div>
          </div>
          <div class="flex flex-row justify-end">
            <ButtonControl
              :disabled="isButtonDisabled"
              @click="validate"
              theme="success"
              :text="$t('btn002')"
            />
          </div>
        </div>
      </ContentSection>
    </div>

    <FooterSection />

    <!-- Keyboard Component -->
    <transition name="slide-in">
      <KeyboardControl
        v-if="keyboard_visible"
        :password="active_field === 'password'"
        :placeholder="
          active_field === 'username'
            ? 'e.g. your-username'
            : 'e.g. password123'
        "
        :inputted_value="
          active_field === 'username' ? form.username : form.password
        "
        @close="closeKeyboard"
        @submit="updateField"
      >
        <div class="flex flex-row items-center justify-between mb-10 w-full">
          <h2 class="text-primary uppercase text-5xl font-bold">
            {{ active_field === "username" ? $t("pma003") : $t("pma004") }}
          </h2>
          <IconControl
            @click="closeKeyboard"
            id="exit-black"
            :width="50"
            :height="50"
          />
        </div>
      </KeyboardControl>
    </transition>
  </div>
</template>

<script>
import SidebarSection from "@/components/sections/SidebarSection.vue";
import ContentSection from "@/components/sections/ContentSection.vue";
import FooterSection from "@/components/sections/FooterSection.vue";
import LabelControl from "@/components/controls/LabelControl.vue";
import ButtonControl from "@/components/controls/ButtonControl.vue";
import KeyboardControl from "@/components/controls/KeyboardControl.vue";
import IconControl from "@/components/controls/IconControl.vue";

import mixin from "@/mixins/controls.js";
import user from "@/services/user";

export default {
  mixins: [mixin],
  components: {
    SidebarSection,
    ContentSection,
    FooterSection,
    LabelControl,
    ButtonControl,
    KeyboardControl,
    IconControl,
  },
  data() {
    return {
      beep: new Audio(
        "https://resource.pafcpic.net/assets/pia/sounds/papsi_beep.mp3"
      ),
      inactivity_timeout: null,
      keyboard_visible: false,
      active_field: null, // Track which field is being edited
      show_password: false,
      form: {
        username: "",
        password: "",
      },
    };
  },
  inject: ["setLoading", "setError"],
  mounted() {
    this.$emit("checkauth", false);
    this.startInactivityTimer();
    window.addEventListener("touchstart", this.resetInactivityTimer);
  },
  beforeUnmount() {
    clearTimeout(this.inactivity_timeout);
    window.removeEventListener("touchstart", this.resetInactivityTimer);
  },
  methods: {
    playBeep() {
      this.beep.play();
    },
    resetInactivityTimer() {
      clearTimeout(this.inactivity_timeout);
      this.startInactivityTimer();
    },
    startInactivityTimer() {
      if (process.env.VUE_APP_DEVELOPMENT_MODE === "false") {
        this.inactivity_timeout = setTimeout(() => {
          this.$router.push({ name: "start-page" });
        }, process.env.VUE_APP_IDLE_DELAY || 30000);
      }
    },
    openKeyboard(field) {
      this.active_field = field;
      this.keyboard_visible = true;
    },
    closeKeyboard() {
      this.keyboard_visible = false;
      this.active_field = null;
    },
    updateField(value) {
      if (this.active_field) {
        this.form[this.active_field] = value;
      }
      this.closeKeyboard();
    },
    togglePasswordVisibility() {
      this.show_password = !this.show_password;
    },
    validate() {
      this.playBeep();
      this.setLoading(true);
      user
        .validateCredentials(this.form.username, this.form.password, 1)
        .then((response) => {
          this.login(
            response.data.data.hash,
            this.form.username,
            this.form.password
          );
        })
        .catch((error) => {
          this.showError(error.response.data.message);
          this.setLoading(false);
        });
    },
    login(hash, username, password) {
      user
        .createSession(hash, username, password, 1)
        .then((response) => {
          this.saveUserData(response.data.data);
          this.setLoading(false);
          this.$router.push({ name: "services-page" });
          this.showSuccess("Login successful!");
        })
        .catch((error) => {
          this.showError(error.response.data.message);
          this.setLoading(false);
        });
    },
  },
  computed: {
    isButtonDisabled() {
      return !this.form.username || !this.form.password;
    },
  },
};
</script>

<style scoped>
.papsi-mascot {
  bottom: 0;
  left: 0;
  width: 25rem;
  z-index: 50;
}

.papsi-eye {
  top: 50%;
  right: 2rem; /* Adjust the value to your preference */
  transform: translateY(-50%);
}
</style>
